import { AttachFile, Cancel, Download, Save } from "@mui/icons-material";
import { Button, FormControlLabel, Switch, Typography } from "@mui/material";
import _ from "lodash";
import React, { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { PipelineConfig } from "../../interfaces/doc";
import { completeConfig } from "../Orion/Config/ConfigDefault";
import {
  useConfigModeContext,
  useFormConfigContext,
} from "../Orion/Config/ConfigFormContext";
import { PageHeader } from "./PageHeader";
import { PageHeaderToolbar } from "./PageHeaderToolbar";

function ConfigToolbar() {
  const {
    reset,
    setValue,
    getValues,
    watch,
    formState: { isSubmitting, isSubmitSuccessful, dirtyFields },
  } = useFormConfigContext();
  const isDirty = _.keys(dirtyFields).length > 0;
  const { isNew, isVisMode, setIsVisMode } = useConfigModeContext();

  useEffect(() => {
    reset(getValues());
  }, [isSubmitSuccessful, getValues, reset]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target?.files?.[0];
    if (file) {
      const fileReader = new FileReader();

      fileReader.onload = (loadEvent) => {
        try {
          setValue(
            "config",
            completeConfig(
              JSON.parse(loadEvent.target?.result as string) as PipelineConfig
            )
          );
        } catch (error) {
          alert("Error parsing JSON file.");
        }
      };

      fileReader.readAsText(file);
    } else {
      alert("Please upload a valid JSON file.");
    }
  };

  const handleDownload = () => {
    const formData = watch();
    const jsonString = JSON.stringify(formData.config, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${formData.name}.json`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <PageHeaderToolbar>
      <div className="flex gap-6">
        <div className="flex gap-2">
          {isNew ? (
            <>
              <Button
                variant="contained"
                type="submit"
                size="small"
                disabled={isSubmitting}
                className="gap-2"
              >
                <Save />
                Créer
              </Button>
              <input
                accept="application/json"
                type="file"
                onChange={handleFileUpload}
                style={{ display: "none" }}
                ref={fileInputRef}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<AttachFile />}
                onClick={() => {
                  fileInputRef.current && fileInputRef.current.click();
                }}
              >
                Upload
              </Button>
            </>
          ) : (
            isDirty && (
              <>
                <Button
                  variant="contained"
                  type="submit"
                  size="small"
                  disabled={isSubmitting}
                  className="gap-2"
                >
                  <Save />
                  Sauvegarder
                </Button>
                <Button
                  variant="contained"
                  disabled={isSubmitting}
                  className="gap-2"
                  color="error"
                  size="small"
                  onClick={(e) => {
                    reset();
                  }}
                >
                  <Cancel />
                  Annuler
                </Button>
              </>
            )
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            onClick={handleDownload}
          >
            Download
          </Button>
        </div>
        <FormControlLabel
          className="font-semibold"
          control={
            <Switch
              checked={isVisMode}
              onChange={(e) => setIsVisMode((prev) => !prev)}
            />
          }
          label={
            <Typography className="font-semibold">Mode Developpeur</Typography>
          }
        />
      </div>
    </PageHeaderToolbar>
  );
}

export function ConfigHeader() {
  const { watch } = useFormConfigContext();
  const location = useLocation();
  const name = watch("name");
  const PageToolbarComponent = useCallback(() => <ConfigToolbar />, []);
  return (
    <PageHeader
      title={name}
      breadcrumbs={[
        { title: "Documents", path: "/orion" },
        { title: "Configurations", path: "/orion/configs" },
        { title: name, path: location.pathname },
      ]}
      slots={{ toolbar: PageToolbarComponent }}
    />
  );
}
